import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import Tabs from "@cloudscape-design/components/tabs";
import TextFilter from "@cloudscape-design/components/text-filter";
import Toggle from "@cloudscape-design/components/toggle";
import Container from "@cloudscape-design/components/container";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Badge from "@cloudscape-design/components/badge";
import Grid from "@cloudscape-design/components/grid";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { userStore } from "../stores/access";
import { gameSectionStore } from "../stores/gamesSection";
import { gamesSectionModalStore } from "../stores/gamesSectionModal";
import {
	AUDIENCE_OPTIONS,
	DEFAULT_ORDER_CONTEXT_OPTIONS,
	PAGE_COLORS,
	PAGE_OPTIONS,
	createEmptyOrderBuckets,
	createGameSectionFormData,
	isGameSectionActive,
	mapOrderContextOptions,
	normalizeSectionPageList,
	normalizeStatusOption,
} from "../helpers/gamesSectionAdmin";

import GamesSectionModal from "./GamesSectionModal";

const SegmentedControl = ({ options, value, onChange, label }) => (
	<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
		{label && (
			<span style={{ fontSize: "12px", fontWeight: 700, color: "#5f6b7a", textTransform: "uppercase", letterSpacing: "0.6px" }}>
				{label}
			</span>
		)}
		<div style={{ display: "inline-flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #d5dbdb" }}>
			{options.map((opt, i) => (
				<button
					key={opt.value}
					type="button"
					onClick={() => onChange(opt.value)}
					style={{
						padding: "6px 18px",
						border: "none",
						borderRight: i < options.length - 1 ? "1px solid #d5dbdb" : "none",
						background: value === opt.value ? "#0972d3" : "#fff",
						color: value === opt.value ? "#fff" : "#414d5c",
						cursor: "pointer",
						fontSize: "13px",
						fontWeight: value === opt.value ? 600 : 400,
						transition: "background 0.15s, color 0.15s",
						whiteSpace: "nowrap",
						outline: "none",
					}}
				>
					{opt.label}
				</button>
			))}
		</div>
	</div>
);

const SectionIcon = ({ src, size = 18 }) => (
	<div style={{
		width: size + 8, height: size + 8,
		background: "#f2f3f3", borderRadius: "6px",
		display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
	}}>
		{src
			? <img src={src} alt="" style={{ width: size, height: size, objectFit: "contain", display: "block" }} />
			: <span style={{ color: "#9ba7b6", fontSize: size * 0.7 }}>—</span>}
	</div>
);

const LOBBY_FIXED_SYSTEM_KEYS = ["search", "categories"];

const isLobbyFixedSystemItem = (item, page) =>
	page === "lobby" && item?.type === "system" && LOBBY_FIXED_SYSTEM_KEYS.includes(item?.key);

const normalizeActiveItemsOrder = (items, page) => {
	if (page !== "lobby") return items;

	const fixedItems = LOBBY_FIXED_SYSTEM_KEYS
		.map((key) => items.find((item) => item.type === "system" && item.key === key))
		.filter(Boolean);

	const fixedKeys = new Set(fixedItems.map((item) => item.key));
	const restItems = items.filter((item) => !(item.type === "system" && fixedKeys.has(item.key)));

	return [...fixedItems, ...restItems];
};

function GamesSection({ Interface }) {
	const user = useRecoilValue(userStore);
	const gameSection = useRecoilValue(gameSectionStore);
	const setGameSection = useSetRecoilState(gameSectionStore);
	const config = useRecoilValue(configStore);
	const setConfig = useSetRecoilState(configStore);
	const setModal = useSetRecoilState(gamesSectionModalStore);

	const [activeTab, setActiveTab] = useState("list");
	const [filterText, setFilterText] = useState("");
	const [sorting, setSorting] = useState({ sortingColumn: { sortingField: "" }, isDescending: false });
	const [listPage, setListPage] = useState(1);

	const [placePage, setPlacePage] = useState("main");
	const [placeAudience, setPlaceAudience] = useState("authorized");
	const [orderLoaded, setOrderLoaded] = useState(false);
	const [orderLoading, setOrderLoading] = useState(false);
	const [orderError, setOrderError] = useState("");
	const [orderOpts, setOrderOpts] = useState(DEFAULT_ORDER_CONTEXT_OPTIONS);
	const [systemByCtx, setSystemByCtx] = useState(createEmptyOrderBuckets());
	const [orderByCtx, setOrderByCtx] = useState(createEmptyOrderBuckets());
	const [dragUid, setDragUid] = useState(null);

	const loadingRef = useRef(false);
	const prevDataRef = useRef(gameSection.data);

	useEffect(() => { Interface.loadGamesSection(); }, []);

	useEffect(() => {
		if (prevDataRef.current !== gameSection.data) {
			prevDataRef.current = gameSection.data;
			if (orderLoaded) setOrderLoaded(false);
			if (orderError) setOrderError("");
		}
	}, [gameSection.data, orderError, orderLoaded]);

	const loadOrder = useCallback(() => {
		if (loadingRef.current) return;
		loadingRef.current = true;
		setOrderLoading(true);
		setOrderError("");

		Interface.loadGamesSectionOrderModel()
			.then((raw) => {
				const m = raw?.data || raw || {};
				const opts = mapOrderContextOptions(m?.orderOptions);
				const sys = createEmptyOrderBuckets(opts);
				const ord = createEmptyOrderBuckets(opts);

				opts.forEach((o) => {
					sys[o.value] = m?.availableSystemItems?.[o.value] || [];
					ord[o.value] = (m?.[o.value] || []).map((item) => ({
						...item,
						_uid: item.type === "custom" ? `custom:${item.id}` : `system:${item.key}`,
					}));
				});

				setOrderOpts(opts);
				setSystemByCtx(sys);
				setOrderByCtx(ord);
				setOrderLoaded(true);
			})
			.catch((err) => {
				const message = typeof err?.text === "string" ? err.text : "Couldn't load placement data";
				setOrderError(message);
			})
			.finally(() => {
				loadingRef.current = false;
				setOrderLoading(false);
			});
	}, [Interface]);

	useEffect(() => {
		if (activeTab === "placement" && !orderLoaded && !orderLoading && !orderError) loadOrder();
	}, [activeTab, loadOrder, orderError, orderLoaded, orderLoading]);

	const ctxKey = `${placePage}_${placeAudience}`;
	const activeItems = useMemo(() => normalizeActiveItemsOrder(orderByCtx[ctxKey] || [], placePage), [orderByCtx, ctxKey, placePage]);

	const activeCustomIds = useMemo(() => new Set(activeItems.filter((i) => i.type === "custom").map((i) => i.id)), [activeItems]);
	const activeSystemKeys = useMemo(() => new Set(activeItems.filter((i) => i.type === "system").map((i) => i.key)), [activeItems]);

	const availableCustom = useMemo(() =>
		(gameSection.data || [])
			.filter((s) => normalizeSectionPageList(s.page).includes(placePage) && !activeCustomIds.has(s.id))
			.map((s) => ({ type: "custom", id: s.id, name: s.name, icon: s.icon, status: s.status, _uid: `custom:${s.id}` })),
		[gameSection.data, placePage, activeCustomIds]
	);

	const availableSystem = useMemo(() =>
		(systemByCtx[ctxKey] || [])
			.filter((i) => !activeSystemKeys.has(i.key))
			.map((i) => ({ ...i, _uid: `system:${i.key}` })),
		[systemByCtx, ctxKey, activeSystemKeys]
	);

	const availableItems = useMemo(() => [...availableCustom, ...availableSystem], [availableCustom, availableSystem]);

	const moveItem = (fromUid, toUid) => {
		if (!fromUid || !toUid || fromUid === toUid) return;
		const items = [...activeItems];
		const fi = items.findIndex((i) => i._uid === fromUid);
		const ti = items.findIndex((i) => i._uid === toUid);
		if (fi < 0 || ti < 0) return;
		if (isLobbyFixedSystemItem(items[fi], placePage) || isLobbyFixedSystemItem(items[ti], placePage)) return;
		const [moved] = items.splice(fi, 1);
		items.splice(ti, 0, moved);
		setOrderByCtx({ ...orderByCtx, [ctxKey]: normalizeActiveItemsOrder(items, placePage) });
	};

	const activate = (item) => {
		const nextItems = normalizeActiveItemsOrder([...activeItems, { ...item }], placePage);
		setOrderByCtx({ ...orderByCtx, [ctxKey]: nextItems });
	};
	const deactivate = (uid) => setOrderByCtx({ ...orderByCtx, [ctxKey]: activeItems.filter((i) => i._uid !== uid) });

	const saveOrder = () => {
		if (orderLoading) return;
		setOrderLoading(true);
		const items = activeItems.map((i) => i.type === "custom" ? { type: "custom", id: i.id } : { type: "system", key: i.key });

		Interface.saveGamesSectionOrderModel(ctxKey, items)
			.then(() => {
				Interface.addNoty("Success", "Placement saved");
				Interface.loadGamesSection();
				setOrderError("");
				setOrderLoaded(false);
			})
			.catch((err) => {
				if (typeof err?.text !== "string") Interface.addNoty("Error", "Couldn't save placement");
			})
			.finally(() => setOrderLoading(false));
	};

	const toggleStatus = async (item) => {
		const on = isGameSectionActive(item);
		try {
			await Interface.request("get", { class: "data", module: "gamesSection", method: "edit" }, {
				id: item.id, name: item.name, status: on ? "inactive" : "active", icon: item.icon,
				page: normalizeSectionPageList(item.page),
				priority_by_page: item.priority_by_page && typeof item.priority_by_page === "object" ? item.priority_by_page : {},
			});
			Interface.loadGamesSection();
			Interface.addNoty("Success", `Section ${on ? "paused" : "activated"}`);
		} catch (err) {
			if (typeof err?.text !== "string") Interface.addNoty("Error", "Failed to change status");
		}
	};

	const openAdd = () => setModal({
		show: true, mode: "add", loaded: true,
		formData: createGameSectionFormData(),
	});

	const openEdit = (item) => setModal({
		show: true, mode: "edit", loaded: true,
		formData: createGameSectionFormData(item),
	});

	const openDuplicate = (item) => {
		const c = cloneDeep(item);
		setModal({
			show: true, mode: "add", loaded: true,
			formData: createGameSectionFormData(c, {
				date: new Date(),
				id: 0,
				name: (c.name || "") + "-copy",
				status: normalizeStatusOption("inactive"),
			}),
		});
	};

	const handleDelete = async (item) => {
		if (!window.confirm(`Delete section "${item.name}"? This cannot be undone.`)) return;
		try {
			await Interface.deleteGamesSection(item.id);
			Interface.loadGamesSection();
			Interface.addNoty("Success", `Section "${item.name}" deleted`);
		} catch (err) {
			if (typeof err?.text !== "string") Interface.addNoty("Error", "Failed to delete section");
		}
	};

	const perPage = config.gameSection?.perPage || 15;

	const filtered = useMemo(() => {
		if (!filterText) return gameSection.data || [];
		const q = filterText.toLowerCase();
		return (gameSection.data || []).filter((item) =>
			item.id?.toString().includes(q) ||
			item.name?.toLowerCase().includes(q) ||
			normalizeSectionPageList(item.page).join(" ").includes(q)
		);
	}, [gameSection.data, filterText]);

	const sorted = useMemo(() => {
		const f = sorting.sortingColumn?.sortingField;
		if (!f) return filtered;
		return [...filtered].sort((a, b) => {
			let va, vb;
			if (f === "page") { va = normalizeSectionPageList(a.page).join(","); vb = normalizeSectionPageList(b.page).join(","); }
			else if (f === "date") { va = +new Date(a.date || 0); vb = +new Date(b.date || 0); }
			else { va = a[f]; vb = b[f]; }
			return (sorting.isDescending ? -1 : 1) * (va < vb ? -1 : va > vb ? 1 : 0);
		});
	}, [filtered, sorting]);

	const paged = sorted.slice((listPage - 1) * perPage, listPage * perPage);

	const renderListTab = () => (
		<Table
			stickyHeader
			loading={!gameSection.loaded}
			sortingColumn={{ sortingField: sorting.sortingColumn?.sortingField }}
			sortingDescending={sorting.isDescending}
			onSortingChange={({ detail }) => setSorting(detail)}
			columnDefinitions={[
				{ id: "id", header: "ID", sortingField: "id", cell: (i) => <span style={{ fontFamily: "monospace" }}>{i.id}</span>, width: 70 },
				{ id: "icon", header: "", cell: (i) => <SectionIcon src={i.icon} />, width: 50 },
				{ id: "name", header: "Slug", sortingField: "name", cell: (i) => <span style={{ fontFamily: "monospace", fontWeight: 500 }}>{i.name}</span> },
				{
					id: "page", header: "Pages", sortingField: "page",
					cell: (i) => (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{normalizeSectionPageList(i.page).map((p) => (
								<span key={p} style={{
									padding: "1px 7px", borderRadius: "4px", fontSize: "11px", fontWeight: 600,
									color: "#fff", background: PAGE_COLORS[p] || "#5f6b7a",
									textTransform: "uppercase", letterSpacing: "0.3px",
								}}>
									{PAGE_OPTIONS.find((o) => o.value === p)?.label || p}
								</span>
							))}
						</div>
					),
				},
				{
					id: "status", header: "Status", sortingField: "status",
					cell: (i) => (
						<Toggle checked={isGameSectionActive(i)} onChange={() => toggleStatus(i)}>
							{isGameSectionActive(i) ? "Active" : "Inactive"}
						</Toggle>
					),
				},
				{ id: "date", header: "Created", sortingField: "date", cell: (i) => new Date(i.date).toLocaleDateString(), width: 110 },
				{
					id: "actions", header: "Actions",
					cell: (i) => (
						<ButtonDropdown
							items={[
								{ text: "Edit", id: "edit" },
								{ text: "Duplicate", id: "duplicate" },
								{ text: "Delete", id: "delete" },
							]}
							onItemClick={(e) => {
								if (e.detail.id === "edit") openEdit(i);
								else if (e.detail.id === "duplicate") openDuplicate(i);
								else if (e.detail.id === "delete") handleDelete(i);
							}}
							expandToViewport
						>
							Actions
						</ButtonDropdown>
					),
					minWidth: 150,
				},
			]}
			items={paged}
			pagination={
				<Pagination
					currentPageIndex={listPage}
					pagesCount={Math.ceil(sorted.length / perPage) || 1}
					onChange={({ detail }) => setListPage(detail.currentPageIndex)}
				/>
			}
			preferences={
				<Preferences
					cancelLabel="Cancel" confirmLabel="Confirm"
					pageSizePreference={{
						title: "Per page",
						options: config.gameSection?.preferences?.perPage || [{ value: 15, label: "15" }, { value: 30, label: "30" }, { value: 50, label: "50" }],
					}}
					preferences={{ pageSize: perPage }}
					title="Preferences"
					onConfirm={({ detail }) => {
						localStorage.setItem("gamesSection.perPage", detail.pageSize);
						setConfig({ ...config, gameSection: { ...config.gameSection, perPage: detail.pageSize } });
						setListPage(1);
					}}
				/>
			}
			filter={
				<TextFilter
					filteringText={filterText}
					filteringPlaceholder="Search by name, ID or page..."
					onChange={({ detail }) => { setFilterText(detail.filteringText); setListPage(1); }}
				/>
			}
			header={
				<Header
					counter={`(${sorted.length})`}
					description="Section definitions — slug, icon, global status and page assignment"
					actions={<Button variant="primary" onClick={openAdd} disabled={!gameSection.loaded}>Add Section</Button>}
				>
					Sections
				</Header>
			}
			empty="No sections found"
		/>
	);

	const renderPlacementTab = () => (
		<Container
			header={
				<Header
					variant="h2"
					description="Enable or disable sections per page and audience, then drag to set display order"
					actions={
						<Button variant="primary" loading={orderLoading} disabled={orderLoading || !orderLoaded} onClick={saveOrder}>
							Save Changes
						</Button>
					}
				>
					Section Placement
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
					<SegmentedControl label="Page" options={PAGE_OPTIONS} value={placePage} onChange={setPlacePage} />
					<SegmentedControl label="Audience" options={AUDIENCE_OPTIONS} value={placeAudience} onChange={setPlaceAudience} />
				</div>

				{orderLoading && !orderLoaded ? (
					<Box textAlign="center" padding={{ vertical: "xl" }} color="text-body-secondary">Loading placement data...</Box>
				) : orderError ? (
					<Alert
						type="error"
						action={
							<Button
								onClick={() => {
									setOrderError("");
									setOrderLoaded(false);
								}}
							>
								Retry
							</Button>
						}
					>
						{orderError}
					</Alert>
				) : !orderLoaded ? (
					<Alert type="info">Switch to this tab to load placement data.</Alert>
				) : (
					<Grid gridDefinition={[{ colspan: 7 }, { colspan: 5 }]}>
						<div>
							<div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
								<span style={{ fontSize: "13px", fontWeight: 700, color: "#16191f" }}>Active</span>
								<span style={{ background: "#037f0c", color: "#fff", padding: "0 7px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, lineHeight: "18px" }}>
									{activeItems.length}
								</span>
							</div>
							<div style={{
								minHeight: 100, border: "2px solid rgba(3,127,12,0.15)", borderRadius: "10px", padding: "6px",
								background: activeItems.length ? "#f7fdf7" : "#fafbfc",
							}}>
								{activeItems.length === 0 ? (
									<div style={{ padding: "40px 16px", textAlign: "center", color: "#9ba7b6", fontSize: "13px" }}>
										No sections enabled for this context.<br />Move sections from Available →
									</div>
								) : (
									<SpaceBetween direction="vertical" size="xxs">
										{activeItems.map((item, idx) => {
											const isDragDisabled = isLobbyFixedSystemItem(item, placePage);

											return (
											<div
												key={item._uid}
												draggable={!isDragDisabled}
												onDragStart={isDragDisabled ? undefined : (e) => { e.dataTransfer.setData("text/plain", item._uid); setDragUid(item._uid); }}
												onDragEnd={() => setDragUid(null)}
												onDragOver={isDragDisabled ? undefined : (e) => e.preventDefault()}
												onDrop={isDragDisabled ? undefined : (e) => { e.preventDefault(); moveItem(e.dataTransfer.getData("text/plain"), item._uid); }}
												style={{
													display: "flex", alignItems: "center", gap: "8px",
													padding: "7px 10px", borderRadius: "8px",
													border: isDragDisabled ? "1px solid transparent" : dragUid === item._uid ? "1.5px dashed #0972d3" : "1px solid #e9ebed",
													background: isDragDisabled ? "#f1f4f8" : item.type === "system" ? "#eef5ff" : "#fff",
													cursor: isDragDisabled ? "default" : "grab",
													opacity: item.type === "custom" && !isGameSectionActive(item) ? 0.5 : 1,
													transition: "box-shadow 0.12s, border-color 0.12s",
												}}
												onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)"; }}
												onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
											>
												<span style={{ color: isDragDisabled ? "#9aa5b5" : "#b4b4b4", fontSize: "12px", cursor: isDragDisabled ? "not-allowed" : "grab", userSelect: "none", lineHeight: 1, opacity: isDragDisabled ? 0.65 : 1 }} title={isDragDisabled ? "This block is fixed for Lobby and cannot be moved" : undefined}>⠿</span>
												<span style={{ width: "18px", textAlign: "center", fontSize: "11px", fontWeight: 700, color: "#5f6b7a" }}>{idx + 1}</span>
												{item.icon && <SectionIcon src={item.icon} size={16} />}
												<span style={{ flex: 1, fontWeight: 500, fontSize: "13px", color: item.type === "system" ? "#0972d3" : "#16191f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
													{item.type === "system" ? (item.name || item.key) : item.name}
												</span>
												{isDragDisabled && <Badge color="grey">Fixed</Badge>}
												{item.type === "system" && <Badge color="blue">System</Badge>}
												{item.type === "custom" && !isGameSectionActive(item) && <Badge color="grey">Off</Badge>}
												<Button variant="inline-icon" iconName="close" onClick={() => deactivate(item._uid)} ariaLabel="Remove from active" />
											</div>
											);
										})}
									</SpaceBetween>
								)}
							</div>
						</div>

						<div>
							<div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
								<span style={{ fontSize: "13px", fontWeight: 700, color: "#16191f" }}>Available</span>
								<span style={{ background: "#5f6b7a", color: "#fff", padding: "0 7px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, lineHeight: "18px" }}>
									{availableItems.length}
								</span>
							</div>
							<div style={{ minHeight: 100, border: "2px dashed #d5dbdb", borderRadius: "10px", padding: "6px", background: "#fafbfc" }}>
								{availableItems.length === 0 ? (
									<div style={{ padding: "40px 16px", textAlign: "center", color: "#9ba7b6", fontSize: "13px" }}>
										All eligible sections are active.
									</div>
								) : (
									<SpaceBetween direction="vertical" size="xxs">
										{availableItems.map((item) => (
											<div
												key={item._uid}
												style={{
													display: "flex", alignItems: "center", gap: "8px",
													padding: "7px 10px", borderRadius: "8px", border: "1px solid #e9ebed",
													background: item.type === "system" ? "#f5f8ff" : "#fff",
													opacity: item.type === "custom" && !isGameSectionActive(item) ? 0.5 : 1,
												}}
											>
												{item.icon && <SectionIcon src={item.icon} size={16} />}
												<span style={{ flex: 1, fontWeight: 500, fontSize: "13px", color: item.type === "system" ? "#0972d3" : "#5f6b7a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
													{item.type === "system" ? (item.name || item.key) : item.name}
												</span>
												{item.type === "system" && <Badge color="blue">System</Badge>}
												{item.type === "custom" && !isGameSectionActive(item) && <Badge color="grey">Off</Badge>}
												<Button variant="inline-icon" iconName="add-plus" onClick={() => activate(item)} ariaLabel="Add to active" />
											</div>
										))}
									</SpaceBetween>
								)}
							</div>
						</div>
					</Grid>
				)}
			</SpaceBetween>
		</Container>
	);

	return (
		<>
			<GamesSectionModal Interface={Interface} />

			{gameSection.loaded && gameSection.error?.text?.length ? (
				<Box margin={{ bottom: "m" }}>
					<Alert type="error" header="Error loading data">
						<Box variant="p">{gameSection.error.text}</Box>
					</Alert>
				</Box>
			) : null}

			<Tabs
				activeTabId={activeTab}
				onChange={({ detail }) => setActiveTab(detail.activeTabId)}
				tabs={[
					{ id: "list", label: "Sections", content: renderListTab() },
					{ id: "placement", label: "Placement & Order", content: renderPlacementTab() },
				]}
			/>
		</>
	);
}

export default GamesSection;
